import React, { memo, useState, useRef, useMemo } from 'react';
import { Handle, Position, useReactFlow } from '@xyflow/react';
import type { NodeProps } from '@xyflow/react';
import { useTenantView } from '../TenantViewContext';
import { getNodePulseLevel, isNodeWiredToLunaCore } from '../pulseUtils';
import {
  PhoneOff,
  ShieldCheck,
  Flame,
  Zap,
  AlertCircle,
  Clock,
  VolumeX,
  EyeOff,
  ShieldAlert,
  Radio,
  ArrowRight
} from 'lucide-react';

export interface CallGuardNodeData {
  autoHangupSilence?: boolean;
  silenceSeconds?: number;
  antiTokenBurn?: boolean;
  antiDataLeak?: boolean;
  maxCallDurationMinutes?: number;
  politeHangupPhrase?: string;
  autoCleanStaleSessions?: boolean;
  isCustomizable?: boolean;
  [key: string]: unknown;
}

export const CallGuardNode = memo(({ id, data, selected }: NodeProps) => {
  const { setNodes, getEdges, getNodes } = useReactFlow();
  const nodeData = (data || {}) as CallGuardNodeData;
  const [saveIndicator, setSaveIndicator] = useState<string | null>(null);
  const debounceTimerRef = useRef<any>(null);

  // Local draft state to prevent cursor jumping when typing politeHangupPhrase
  const [localHangupPhrase, setLocalHangupPhrase] = useState<string>(() => nodeData.politeHangupPhrase || '');
  const isTypingRef = useRef<boolean>(false);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const syncDebounceTimerRef = useRef<any>(null);

  React.useEffect(() => {
    if (!isTypingRef.current && nodeData.politeHangupPhrase !== undefined && nodeData.politeHangupPhrase !== localHangupPhrase) {
      setLocalHangupPhrase(nodeData.politeHangupPhrase);
    }
  }, [nodeData.politeHangupPhrase]);

  const { isTenantView, authToken, tenantApiKey, pulseState, openPortalData, isCompactView, openInspector } = useTenantView();

  const persistNodeDataDirectly = (field: keyof CallGuardNodeData, value: any, allUpdates?: Partial<CallGuardNodeData>) => {
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    debounceTimerRef.current = setTimeout(async () => {
      try {
        const payloadData = {
          ...nodeData,
          [field]: value,
          ...(allUpdates || {})
        };

        const headers: Record<string, string> = {
          'Content-Type': 'application/json',
        };

        const token = authToken || (typeof localStorage !== 'undefined' ? localStorage.getItem('tenet_token') || localStorage.getItem('admin_token') : null);
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }
        if (tenantApiKey) {
          headers['x-tenant-api-key'] = tenantApiKey;
        }

        const endpoint = isTenantView ? '/api/tenet/widgets/save-node-data' : '/api/admin/luna-flows/save-node-data';
        const res = await fetch(endpoint, {
          method: 'POST',
          headers,
          body: JSON.stringify({
            nodeId: id,
            widgetType: 'CALL_GUARD',
            data: payloadData,
            widgetData: payloadData,
          }),
        });

        if (res.ok) {
          setSaveIndicator('✅ Saved');
          setTimeout(() => setSaveIndicator(null), 3000);
        }
      } catch (err) {
        console.warn('[CallGuardNode] Direct persist warning:', err);
      }
    }, 600);
  };

  const handleChange = (field: keyof CallGuardNodeData, value: any) => {
    setNodes((nds) =>
      nds.map((n) => (n.id === id ? { ...n, data: { ...n.data, [field]: value } } : n))
    );
    persistNodeDataDirectly(field, value);
  };

  const handleHangupPhraseChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const nextVal = e.target.value;
    const cursor = e.target.selectionStart;
    setLocalHangupPhrase(nextVal);

    if (syncDebounceTimerRef.current) clearTimeout(syncDebounceTimerRef.current);
    syncDebounceTimerRef.current = setTimeout(() => {
      setNodes((nds) =>
        nds.map((n) => (n.id === id ? { ...n, data: { ...n.data, politeHangupPhrase: nextVal } } : n))
      );
      persistNodeDataDirectly('politeHangupPhrase', nextVal);
    }, 450);

    requestAnimationFrame(() => {
      if (textareaRef.current && document.activeElement === textareaRef.current) {
        textareaRef.current.setSelectionRange(cursor, cursor);
      }
    });
  };

  const handleHangupPhraseFocus = () => {
    isTypingRef.current = true;
  };

  const handleHangupPhraseBlur = () => {
    isTypingRef.current = false;
    if (syncDebounceTimerRef.current) clearTimeout(syncDebounceTimerRef.current);
    setNodes((nds) =>
      nds.map((n) => (n.id === id ? { ...n, data: { ...n.data, politeHangupPhrase: localHangupPhrase } } : n))
    );
    persistNodeDataDirectly('politeHangupPhrase', localHangupPhrase);
  };

  const autoHangupSilence = nodeData.autoHangupSilence !== false;
  const silenceSeconds = Number(nodeData.silenceSeconds) || 15;
  const antiTokenBurn = nodeData.antiTokenBurn !== false;
  const antiDataLeak = nodeData.antiDataLeak !== false;
  const maxCallDurationMinutes = Number(nodeData.maxCallDurationMinutes) || 10;
  const autoCleanStaleSessions = nodeData.autoCleanStaleSessions !== false;

  const pulseLevel = getNodePulseLevel(id, pulseState);
  const isNodeActive = pulseLevel === 'active';
  const isNodeTrail = pulseLevel === 'trail';
  const isWired = isNodeWiredToLunaCore(id, getEdges(), getNodes());

  return (
    <div
      className={`group rounded-2xl border transition-all duration-300 w-[360px] select-none relative backdrop-blur-xl bg-gradient-to-b from-[#180A0E]/98 via-[#0F0608]/98 to-[#080304]/98 shadow-[0_20px_50px_rgba(0,0,0,0.85),inset_0_1px_0_0_rgba(255,255,255,0.12)] ${
        isNodeActive
          ? 'border-cyan-300 ring-4 ring-cyan-400/60 shadow-[0_0_80px_rgba(34,211,238,0.8),inset_0_1px_0_0_rgba(255,255,255,0.2)] scale-[1.02] pulse-glow-active'
          : isNodeTrail
          ? 'border-emerald-400/70 ring-2 ring-emerald-400/30 shadow-[0_0_40px_rgba(52,211,153,0.4),inset_0_1px_0_0_rgba(255,255,255,0.15)]'
          : selected
          ? 'border-rose-400 ring-2 ring-rose-400/50 shadow-[0_0_35px_rgba(244,63,94,0.5),inset_0_1px_0_0_rgba(255,255,255,0.18)]'
          : isWired
          ? 'border-rose-500/40 hover:border-rose-400 shadow-[0_20px_50px_rgba(0,0,0,0.85),0_0_20px_rgba(244,63,94,0.15),inset_0_1px_0_0_rgba(255,255,255,0.12)]'
          : 'border-white/[0.08] hover:border-white/[0.18]'
      }`}
    >
      {/* ── Floating Cyber-HUD Quick Jump ── */}
      {openPortalData && (
        <div className="absolute -top-11 left-1/2 -translate-x-1/2 z-50 opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto transition-all duration-300 transform translate-y-1 group-hover:translate-y-0">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              openPortalData('call_guard', id);
            }}
            className="nodrag flex items-center gap-2 px-4 py-1.5 rounded-full bg-gradient-to-r from-rose-950 via-slate-900 to-amber-950 border-2 border-rose-400 text-rose-200 font-extrabold text-[11px] uppercase tracking-wider shadow-[0_0_25px_rgba(244,63,94,0.8)] hover:border-rose-300 hover:scale-105 hover:text-white transition-all cursor-pointer whitespace-nowrap backdrop-blur-md active:scale-95"
            title="Open Portal Data tab to view call defense logs"
          >
            <Zap size={13} className="text-rose-300 animate-pulse" />
            <span>Call Defense Studio</span>
            <ArrowRight size={13} className="text-rose-400" />
          </button>
        </div>
      )}
      {/* ── Directional Connection Handles (Left in / Right out to Luna Core) ── */}
      <Handle
        type="target"
        position={Position.Left}
        id="in-callguard"
        className="!bg-rose-400 !w-4 !h-4 !border-2 !border-slate-950 !-left-2 transition-transform hover:scale-125 z-20 shadow-[0_0_12px_rgba(244,63,94,1)]"
        title="Input: Inbound Call Security"
      />
      <Handle
        type="source"
        position={Position.Right}
        id="out-callguard"
        className="!bg-amber-400 !w-4 !h-4 !border-2 !border-slate-950 !-right-2 transition-transform hover:scale-125 z-20 shadow-[0_0_12px_rgba(245,158,11,1)]"
        title="Output to Luna Core Hub (Call Guard Protection)"
      />

      {/* Target Connection Handle (Top) */}
      <Handle
        type="target"
        position={Position.Top}
        id="in-top"
        className="!bg-rose-400 !w-3.5 !h-3.5 !border-2 !border-slate-950 !-top-2 transition-transform hover:scale-125 shadow-[0_0_8px_rgba(244,63,94,0.8)]"
      />

      {/* ── Header ───────────────────────────────────────────────────── */}
      <div className="bg-gradient-to-r from-rose-950/80 via-slate-900/90 to-orange-950/80 px-4 py-3 border-b border-rose-500/30 flex items-center justify-between rounded-t-2xl">
        <div className="flex items-center gap-2.5">
          <div className="p-1.5 rounded-lg bg-rose-500/20 border border-rose-400/50 text-rose-300 shadow-[0_0_12px_rgba(244,63,94,0.4)]">
            <ShieldAlert size={16} className="animate-pulse text-rose-300" />
          </div>
          <div>
            <h4 className="text-xs font-black text-slate-100 tracking-wide">Call Guard & Anti-Sabotage</h4>
            <p className="text-[10px] text-rose-300/80 font-mono">Live Call & Resource Shield</p>
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          {saveIndicator && (
            <span className="text-[9px] font-mono font-bold text-rose-400 animate-pulse bg-rose-500/10 px-2 py-0.5 rounded-full border border-rose-500/30">
              {saveIndicator}
            </span>
          )}
          <span className="text-[9px] font-black uppercase tracking-wider px-2 py-0.5 rounded-full bg-rose-500/20 text-rose-300 border border-rose-500/40 shadow-[0_0_8px_rgba(244,63,94,0.3)]">
            CALL SHIELD
          </span>
        </div>
      </div>

      {/* Wiring Live Status Bar */}
      <div className="mx-4 mt-3">
        {isWired ? (
          <div className="px-2.5 py-1 rounded-lg bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 text-[10px] flex items-center justify-between font-mono">
            <span className="flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
              <Zap size={11} className="text-emerald-400" /> Armed on Luna Live Voice
            </span>
            <span className="text-[9px] font-bold text-emerald-400 uppercase tracking-wide">ACTIVE</span>
          </div>
        ) : (
          <div className="px-2.5 py-1 rounded-lg bg-rose-500/10 border border-rose-500/30 text-rose-300 text-[10px] flex items-center justify-between font-mono">
            <span className="flex items-center gap-1.5">
              <AlertCircle size={11} className="text-rose-400" /> Disconnected
            </span>
            <span className="text-[9px] text-rose-400/80">Connect to Luna Core Hub</span>
          </div>
        )}
      </div>

      {/* ── Body ─────────────────────────────────────────────────────── */}
      {isCompactView ? (
        <div className="p-4 space-y-2.5 text-xs">
          <div className="p-3 rounded-2xl bg-slate-900/80 border border-rose-500/25 space-y-2.5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <ShieldAlert size={14} className="text-rose-400" />
                <span className="font-bold text-white text-xs">SIP Call Safeguards</span>
              </div>
              <button
                type="button"
                onClick={() => openInspector && openInspector(id)}
                className="nodrag px-2.5 py-1 rounded-lg text-[10px] font-bold bg-rose-500/20 hover:bg-rose-500/35 text-rose-200 border border-rose-400/40 transition cursor-pointer active:scale-95 shadow-sm"
              >
                Inspect ⚙️
              </button>
            </div>

            <div className="flex flex-wrap gap-1.5 pt-1">
              <span className="px-2 py-0.5 rounded-md bg-slate-800 border border-slate-700 text-[10px] font-mono text-rose-300 font-bold">
                ⏱️ Max {maxCallDurationMinutes}m
              </span>
              <span className="px-2 py-0.5 rounded-md bg-slate-800 border border-slate-700 text-[10px] font-mono text-amber-300 font-bold">
                🔇 {silenceSeconds}s Silence Hangup
              </span>
              {antiTokenBurn && (
                <span className="px-2 py-0.5 rounded-md bg-emerald-950/60 border border-emerald-500/30 text-[10px] font-mono text-emerald-400 font-bold">
                  🛡️ Anti-Burn Active
                </span>
              )}
            </div>
          </div>
        </div>
      ) : (
        <div className="p-4 space-y-3 text-xs">
        
        {/* 1. Silence & Inactivity Auto-Hangup Guard */}
        <div className="space-y-2 p-2.5 rounded-xl bg-slate-900/80 border border-rose-500/20">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <VolumeX size={14} className="text-rose-400" />
              <div>
                <span className="text-[11px] font-bold text-slate-100 block">Silence & Ghost Call Auto-Hangup</span>
                <span className="text-[9px] text-slate-400 block leading-tight">
                  Prompt caller if silent, then politely disconnect line
                </span>
              </div>
            </div>
            <input
              type="checkbox"
              checked={autoHangupSilence}
              onChange={(e) => handleChange('autoHangupSilence', e.target.checked)}
              className="nodrag rounded border-rose-500/40 bg-slate-950 text-rose-500 focus:ring-0 cursor-pointer"
            />
          </div>

          {autoHangupSilence && (
            <div className="pt-2 border-t border-white/5 flex items-center justify-between text-[10px]">
              <span className="text-slate-400">Silence Prompt Timeout:</span>
              <select
                value={silenceSeconds}
                onChange={(e) => handleChange('silenceSeconds', Number(e.target.value))}
                className="bg-slate-950 border border-rose-500/30 text-rose-300 rounded px-2 py-0.5 text-[10px] font-mono focus:outline-none"
              >
                <option value={8}>8 Seconds (Fast Prompt)</option>
                <option value={10}>10 Seconds</option>
                <option value={12}>12 Seconds (Recommended)</option>
                <option value={15}>15 Seconds (Standard)</option>
                <option value={20}>20 Seconds</option>
                <option value={30}>30 Seconds</option>
              </select>
            </div>
          )}
        </div>

        {/* 2. Anti-Token Burn & Anti-Trolling Guard */}
        <div
          onClick={() => handleChange('antiTokenBurn', !antiTokenBurn)}
          className="nodrag flex items-center justify-between p-2.5 rounded-xl bg-slate-900/80 border border-rose-500/20 transition-all hover:border-rose-500/40 cursor-pointer"
        >
          <div className="flex items-start gap-2 pr-2">
            <Flame size={14} className="text-orange-400 mt-0.5 shrink-0" />
            <div>
              <span className="text-[11px] font-bold text-slate-100 block">Anti-Token-Burn & Trolling Shield</span>
              <span className="text-[9px] text-slate-400 block leading-tight">
                Detect circular spam, off-topic harassment & cut line politely
              </span>
            </div>
          </div>
          <input
            type="checkbox"
            checked={antiTokenBurn}
            onChange={() => {}}
            className="nodrag rounded border-rose-500/40 bg-slate-950 text-rose-500 focus:ring-0 pointer-events-none shrink-0"
          />
        </div>

        {/* 3. Anti-Data Leak & Social Engineering Shield */}
        <div
          onClick={() => handleChange('antiDataLeak', !antiDataLeak)}
          className="nodrag flex items-center justify-between p-2.5 rounded-xl bg-slate-900/80 border border-rose-500/20 transition-all hover:border-rose-500/40 cursor-pointer"
        >
          <div className="flex items-start gap-2 pr-2">
            <EyeOff size={14} className="text-rose-400 mt-0.5 shrink-0" />
            <div>
              <span className="text-[11px] font-bold text-slate-100 block">Anti-Data-Leak & Secret Shield</span>
              <span className="text-[9px] text-slate-400 block leading-tight">
                Block prompt injection, pity hacks & protect internal margins/APIs
              </span>
            </div>
          </div>
          <input
            type="checkbox"
            checked={antiDataLeak}
            onChange={() => {}}
            className="nodrag rounded border-rose-500/40 bg-slate-950 text-rose-500 focus:ring-0 pointer-events-none shrink-0"
          />
        </div>

        {/* 4. Ghost Session Auto-Clearing */}
        <div
          onClick={() => handleChange('autoCleanStaleSessions', !autoCleanStaleSessions)}
          className="nodrag flex items-center justify-between p-2.5 rounded-xl bg-slate-900/80 border border-rose-500/20 transition-all hover:border-rose-500/40 cursor-pointer"
        >
          <div className="flex items-start gap-2 pr-2">
            <Radio size={14} className="text-cyan-400 mt-0.5 shrink-0" />
            <div>
              <span className="text-[11px] font-bold text-slate-100 block">Auto-Purge Ghost Calls (No Signal Loss)</span>
              <span className="text-[9px] text-slate-400 block leading-tight">
                Instantly clear dropped connections so user never gets &quot;In Another Call&quot;
              </span>
            </div>
          </div>
          <input
            type="checkbox"
            checked={autoCleanStaleSessions}
            onChange={() => {}}
            className="nodrag rounded border-rose-500/40 bg-slate-950 text-rose-500 focus:ring-0 pointer-events-none shrink-0"
          />
        </div>

        {/* 5. Max Call Duration Guard */}
        <div className="pt-2 border-t border-slate-800 flex items-center justify-between text-[11px]">
          <div className="flex items-center gap-1.5 text-slate-400">
            <Clock size={13} className="text-rose-400" />
            <span>Max Call Duration:</span>
          </div>
          <select
            value={maxCallDurationMinutes}
            onChange={(e) => handleChange('maxCallDurationMinutes', Number(e.target.value))}
            className="bg-slate-900 border border-slate-700 text-rose-300 rounded px-2 py-1 text-[11px] font-medium focus:outline-none"
          >
            <option value={3}>3 Minutes</option>
            <option value={5}>5 Minutes</option>
            <option value={10}>10 Minutes (Standard)</option>
            <option value={15}>15 Minutes</option>
            <option value={30}>30 Minutes</option>
            <option value={9999}>Unlimited</option>
          </select>
        </div>

        {/* 6. Customizable Polite Closing Remark */}
        <div>
          <label className="block text-[10px] font-bold uppercase tracking-wider text-rose-300 mb-1 flex items-center gap-1">
            <PhoneOff size={11} className="text-rose-400" /> Polite Disconnection Remark (Sinhala / Eng)
          </label>
          <textarea
            ref={textareaRef}
            rows={2}
            className="nodrag nowheel w-full bg-slate-950 border border-rose-500/20 focus:border-rose-400 rounded-xl px-2.5 py-1.5 text-slate-100 text-[11px] focus:outline-none focus:ring-1 focus:ring-rose-400/30 transition-all placeholder:text-slate-600 resize-none font-sans leading-relaxed"
            placeholder="අපේ අනිත් clients ලාත් line එකේ ඉන්න නිසාත්, මේක අපේ business එකට අදාළ නැති දෙයක් නිසාත් මම call එක end කරනවා. ඔබට අපේ service එක ගැන දැනගන්න ඕන නම් WhatsApp message එකක් දාන්න. Thank you, have a great day!"
            value={localHangupPhrase}
            onChange={handleHangupPhraseChange}
            onFocus={handleHangupPhraseFocus}
            onBlur={handleHangupPhraseBlur}
          />
        </div>

      </div>
      )}

      {/* ── Footer ───────────────────────────────────────────────────── */}
      <div className="px-4 py-2 bg-slate-950/80 border-t border-rose-500/20 rounded-b-2xl flex items-center justify-between text-[10px] text-slate-400 font-mono">
        <span className="flex items-center gap-1 text-rose-400">
          <ShieldCheck size={12} className="text-emerald-400" />
          Autonomous Hangup Tool Active
        </span>
        <span>SIP Live Guard</span>
      </div>
    </div>
  );
});

CallGuardNode.displayName = 'CallGuardNode';
export default CallGuardNode;
